import { MakeLogicType, actions, connect, kea, listeners, path, selectors } from 'kea'
import { loaders } from 'kea-loaders'

import { ApiConfig } from 'lib/api'
import { urls } from 'scenes/urls'

import IconPostHog from 'public/posthog-icon.svg'
import IconDuckDB from 'public/services/duckdb.svg'
import IconMySQL from 'public/services/mysql.png'
import IconPostgres from 'public/services/postgres.png'
import IconRedshift from 'public/services/redshift.png'
import IconSnowflake from 'public/services/snowflake.png'

import { sourcesDataLogic } from 'products/data_warehouse/frontend/shared/logics/sourcesDataLogic'
import { externalDataSourcesConnectionsList } from 'products/warehouse_sources/frontend/generated/api'
import type { ExternalDataSourceConnectionOptionApi } from 'products/warehouse_sources/frontend/generated/api.schemas'

import type { PaginatedResponse } from '../../../lib/api'
import type { ExternalDataSource } from '../../../types'

export const POSTHOG_WAREHOUSE = '__posthog_warehouse__'
export const LOADING_CONNECTIONS = '__loading_connections__'
export const ADD_POSTGRES_DIRECT_CONNECTION = '__add_postgres_direct_connection__'
export const ADD_MYSQL_DIRECT_CONNECTION = '__add_mysql_direct_connection__'
export const ADD_SNOWFLAKE_DIRECT_CONNECTION = '__add_snowflake_direct_connection__'
export const ADD_REDSHIFT_DIRECT_CONNECTION = '__add_redshift_direct_connection__'
export const CONFIGURE_SOURCES = '__configure_sources__'

export interface ConnectionSelectOption {
    // A leaf carries a `value`; a node carries nested `options` and renders as a submenu.
    value?: string
    label: string
    disabled?: boolean
    iconSrc?: string
    managementUrl?: string
    options?: ConnectionSelectOption[]
}

export interface ConnectionSelectOptionGroup {
    options: ConnectionSelectOption[]
}

type ConnectionEngine = 'duckdb' | 'postgres' | 'mysql' | 'snowflake' | 'redshift'

const ENGINE_LABELS: Record<ConnectionEngine, string> = {
    duckdb: 'DuckDB',
    postgres: 'Postgres',
    mysql: 'MySQL',
    snowflake: 'Snowflake',
    redshift: 'Redshift',
}

const ENGINE_ICONS: Record<ConnectionEngine, string> = {
    duckdb: IconDuckDB,
    postgres: IconPostgres,
    mysql: IconMySQL,
    snowflake: IconSnowflake,
    redshift: IconRedshift,
}

function getConnectionEngine(
    source: Pick<ExternalDataSourceConnectionOptionApi, 'engine' | 'source_type'>
): ConnectionEngine {
    if (
        source.engine === 'duckdb' ||
        source.engine === 'mysql' ||
        source.engine === 'snowflake' ||
        source.engine === 'redshift'
    ) {
        return source.engine
    }
    // Synced sources have no detected connection engine — derive it from the source type.
    const sourceTypeEngine = source.source_type?.toLowerCase()
    if (sourceTypeEngine && sourceTypeEngine in ENGINE_LABELS) {
        return sourceTypeEngine as ConnectionEngine
    }
    return 'postgres'
}

export function getConnectionSelectorValue(
    connectionOptions: ExternalDataSourceConnectionOptionApi[] | null,
    connectionOptionsLoading: boolean,
    selectedConnectionId: string | undefined
): string {
    if (connectionOptionsLoading) {
        return LOADING_CONNECTIONS
    }

    if (selectedConnectionId && (connectionOptions ?? []).some((source) => source.id === selectedConnectionId)) {
        return selectedConnectionId
    }

    return POSTHOG_WAREHOUSE
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface connectionSelectorLogicValues {
    connectionOptions: ExternalDataSourceConnectionOptionApi[] | null
    connectionOptionsLoading: boolean
    connectionSelectOptions: ConnectionSelectOptionGroup[]
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface connectionSelectorLogicActions {
    loadSourcesSuccess: (
        dataWarehouseSources:
            | PaginatedResponse<ExternalDataSource>
            | {
                  count: number
                  next: null
                  previous: null
                  results: never[]
              },
        payload?:
            | {
                  value: true
              }
            | undefined
    ) => {
        dataWarehouseSources:
            | PaginatedResponse<ExternalDataSource>
            | {
                  count: number
                  next: null
                  previous: null
                  results: never[]
              }
        payload?: {
            value: true
        }
    } // sourcesDataLogic
    loadConnectionOptions: () => any
    loadConnectionOptionsFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadConnectionOptionsSuccess: (
        connectionOptions: ExternalDataSourceConnectionOptionApi[],
        payload?: any
    ) => {
        connectionOptions: ExternalDataSourceConnectionOptionApi[]
        payload?: any
    }
    maybeLoadConnectionOptions: () => {
        value: true
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface connectionSelectorLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        connectionSelectOptions: (
            connectionOptions: ExternalDataSourceConnectionOptionApi[] | null,
            connectionOptionsLoading: boolean
        ) => ConnectionSelectOptionGroup[]
    }
}

export type connectionSelectorLogicType = MakeLogicType<
    connectionSelectorLogicValues,
    connectionSelectorLogicActions,
    Record<string, any>,
    connectionSelectorLogicMeta
>

export const connectionSelectorLogic = kea<connectionSelectorLogicType>([
    path(['scenes', 'data-warehouse', 'editor', 'connectionSelectorLogic']),
    connect(() => ({
        actions: [sourcesDataLogic, ['loadSourcesSuccess']],
    })),
    actions({
        maybeLoadConnectionOptions: true,
    }),
    loaders(() => ({
        connectionOptions: [
            null as ExternalDataSourceConnectionOptionApi[] | null,
            {
                loadConnectionOptions: async (): Promise<ExternalDataSourceConnectionOptionApi[]> => {
                    try {
                        // The projects route treats the path param as a team id (environments transition),
                        // so pass the current team id to keep per-environment scoping.
                        return await externalDataSourcesConnectionsList(String(ApiConfig.getCurrentTeamId()))
                    } catch (error: any) {
                        if (error?.status === 403) {
                            return []
                        }

                        return []
                    }
                },
            },
        ],
    })),
    selectors({
        connectionSelectOptions: [
            (s) => [s.connectionOptions, s.connectionOptionsLoading],
            (
                connectionOptions: ExternalDataSourceConnectionOptionApi[] | null,
                connectionOptionsLoading: boolean
            ): ConnectionSelectOptionGroup[] => {
                const sourceOptions = connectionOptionsLoading
                    ? [{ value: LOADING_CONNECTIONS, label: 'Loading...', disabled: true }]
                    : (connectionOptions ?? []).map((source) => {
                          const engine = getConnectionEngine(source)
                          const isSynced = source.access_method === 'warehouse'

                          return {
                              value: source.id,
                              label: `${source.prefix ? source.prefix : source.id} (${ENGINE_LABELS[engine]}${
                                  isSynced ? ' · synced' : ''
                              })`,
                              iconSrc: ENGINE_ICONS[engine],
                              managementUrl: urls.dataWarehouseSource(`managed-${source.id}`),
                          }
                      })

                return [
                    {
                        options: [
                            {
                                value: POSTHOG_WAREHOUSE,
                                label: 'PostHog (ClickHouse)',
                                iconSrc: IconPostHog,
                            },
                            ...sourceOptions,
                        ],
                    },
                    {
                        options: [
                            { value: CONFIGURE_SOURCES, label: 'Configure sources' },
                            {
                                label: 'Add direct connection',
                                options: [
                                    {
                                        value: ADD_POSTGRES_DIRECT_CONNECTION,
                                        label: 'Postgres',
                                        iconSrc: IconPostgres,
                                    },
                                    { value: ADD_MYSQL_DIRECT_CONNECTION, label: 'MySQL', iconSrc: IconMySQL },
                                    {
                                        value: ADD_SNOWFLAKE_DIRECT_CONNECTION,
                                        label: 'Snowflake',
                                        iconSrc: IconSnowflake,
                                    },
                                    {
                                        value: ADD_REDSHIFT_DIRECT_CONNECTION,
                                        label: 'Redshift',
                                        iconSrc: IconRedshift,
                                    },
                                ],
                            },
                        ],
                    },
                ]
            },
        ],
    }),
    // No afterMount auto-load: sqlEditorLogic connects this logic, so it mounts with every
    // embedded SQL editor (notebooks, logs, endpoints). Only surfaces that render the
    // connection selector should pay for the fetch — they call maybeLoadConnectionOptions.
    listeners(({ actions, values }) => ({
        maybeLoadConnectionOptions: () => {
            if (values.connectionOptions === null && !values.connectionOptionsLoading) {
                actions.loadConnectionOptions()
            }
        },
        loadSourcesSuccess: () => {
            // Refresh only where the options were fetched in the first place.
            if (values.connectionOptions !== null) {
                actions.loadConnectionOptions()
            }
        },
    })),
])
